import Service from '@ember/service';
import { action } from '@ember/object';
import config from 'cio/config/environment';

type WindowMessage<B = any> = {
  name: string;
  body: B;
};

export type WindowMessageHandler<B = any> = (body: WindowMessage<B>['body']) => void;

export default class WindowMessengerService extends Service {
  private handlers: Map<string, Set<WindowMessageHandler>> = new Map();

  isActive = false;

  willDestroy() {
    this.deactivate(true);
  }

  @action
  activate() {
    window.addEventListener('message', this.handleMessage);
    this.set('isActive', true);
  }

  @action
  deactivate(force?: boolean) {
    if (this.handlers.size === 0 || force) {
      window.removeEventListener('message', this.handleMessage);
      this.set('isActive', false);
    }
  }

  @action
  private handleMessage({ data, origin }: MessageEvent) {
    if (!data.name || (!config.isTest && origin !== 'https://customer.io')) {
      return;
    }

    let eventName = data.name;
    let handlers = this.handlers.get(eventName);

    if (!handlers) {
      return;
    }

    handlers.forEach((handler) => handler(data.body));
  }

  @action
  on<B = any>(event: string, cb: WindowMessageHandler<B>) {
    if (!this.isActive) {
      this.activate();
    }

    let existingHandlers = this.handlers.get(event) ?? new Set();
    let newHandlers = existingHandlers.add(cb);

    this.handlers.set(event, newHandlers);
  }

  @action
  off<B = any>(event: string, cb: WindowMessageHandler<B>) {
    let existingHandlers = this.handlers.get(event);

    if (!existingHandlers) {
      return;
    }

    existingHandlers.delete(cb);

    this.handlers.set(event, existingHandlers);
    this.deactivate();
  }

  @action
  clearAll(event?: string) {
    if (event) {
      this.handlers.delete(event);
    } else {
      this.handlers = new Map();
    }

    this.deactivate();
  }

  @action
  send<B = any>(name: string, body: WindowMessage<B>['body']) {
    if (!window.parent) {
      return;
    }

    window.parent.postMessage({ name, body }, 'https://customer.io');
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    'window-messenger': WindowMessengerService;
  }
}
